import { minutesToMilliseconds } from 'date-fns';
import { useCallback, useRef } from 'react';

import { useTranslate } from '@growtherapy/i18n';
import { Button } from '@growtherapy/sprout-ui';

import {
  ClientInformationDrawerTab as SeedlingClientInformationDrawerTab,
  useSetActiveClientInfoDrawerTab,
} from '@grow-therapy-team/seedling-components';

import { logger } from '../../datadog/logger';
import { useAtomCallbackMemoized } from '../../hooks/useAtomCallbackMemoized';
import { useEndOfSessionToast } from '../../hooks/useEndOfSessionToast';
import { Translate } from '../../i18n';
import { useTrackEvent } from '../../segment/segment';
import { EventTypeEnum } from '../../segment/types';
import { toast } from '../../toasts';
import { roomAtom } from '../../video-sdk';
import { useOpenClientInformationCallback } from '../client-information/useClientInformationDrawerCallbacks';
import { ProviderDrawerState } from '../state';
import { useCachedPatientByShortId } from '../useGetPatientByShortId';
import { PromptFollowUpToast } from './PromptFollowUpToast';
import { useEndOfSessionNudgeExperiment } from './useEndOfSessionNudgeExperiment';
import { useInSessionAppointment } from './useInSessionAppointment';
import { useIsIntakeAppointment } from './useIsIntakeAppointment';
import { formatNextAppointmentDateTime, hasScheduledFollowUp } from './utils';

const NUDGE_PRIOR_END_TIME_MS = minutesToMilliseconds(5);

export function usePromptFollowUpToast(
  providerShortId: string | undefined,
  inSessionPatientInformation?: {
    patientShortId: string;
    visitorUuid?: string;
  },
) {
  const t = useTranslate();
  const getIsInSession = useAtomCallbackMemoized((get) => !!get(roomAtom), []);
  const { patientShortId, visitorUuid } = inSessionPatientInformation ?? {};
  const setSeedlingActiveTab = useSetActiveClientInfoDrawerTab();
  const { userClicked, userViewed } = useTrackEvent();

  const openClientInformation = useOpenClientInformationCallback();
  const shouldSeeIntakeNudge = useEndOfSessionNudgeExperiment();
  const isIntakeAppointment = useIsIntakeAppointment();
  const { data: patientChartData } = useCachedPatientByShortId(patientShortId);
  const inSessionAppointment = useInSessionAppointment();

  const patientChart = patientChartData?.patientChart;
  const nextAppointment = patientChart?.nextAppointment;
  const clientFirstName = patientChart?.firstName ?? '';
  const appointmentEndTime = inSessionAppointment?.timeEnd;
  const appointmentShortId = inSessionAppointment?.appointmentShortId ?? '';
  const hasFollowUpScheduled = hasScheduledFollowUp(nextAppointment, appointmentEndTime);

  const shouldSeeIntakeNudgeRef = useRef(shouldSeeIntakeNudge);
  shouldSeeIntakeNudgeRef.current = shouldSeeIntakeNudge;

  const isIntakeAppointmentRef = useRef(isIntakeAppointment);
  isIntakeAppointmentRef.current = isIntakeAppointment;

  const hasFollowUpScheduledRef = useRef(hasFollowUpScheduled);
  hasFollowUpScheduledRef.current = hasFollowUpScheduled;

  const nextAppointmentRef = useRef(nextAppointment);
  nextAppointmentRef.current = nextAppointment;

  const clientFirstNameRef = useRef(clientFirstName);
  clientFirstNameRef.current = clientFirstName;

  const openAppointments = useCallback(() => {
    if (!patientShortId) {
      logger.error('Missing patient short ID when opening client information drawer (usePromptFollowUpToast)');
      return;
    }
    openClientInformation({ patientShortId, visitorUuid });
    setSeedlingActiveTab(SeedlingClientInformationDrawerTab.APPOINTMENTS);
  }, [openClientInformation, patientShortId, setSeedlingActiveTab, visitorUuid]);

  const openSchedulingDrawer = useCallback(() => {
    if (!patientShortId) {
      logger.error('Missing patient short ID when opening scheduling drawer (usePromptFollowUpToast)');
      return;
    }
    void openClientInformation({ patientShortId, visitorUuid }, ProviderDrawerState.ADD_APPOINTMENT);
  }, [openClientInformation, patientShortId, visitorUuid]);

  const openAppointmentsRef = useRef(openAppointments);
  openAppointmentsRef.current = openAppointments;

  const openSchedulingDrawerRef = useRef(openSchedulingDrawer);
  openSchedulingDrawerRef.current = openSchedulingDrawer;

  const appointmentShortIdRef = useRef(appointmentShortId);
  appointmentShortIdRef.current = appointmentShortId;

  const trackSchedulingPromptRef = useRef(() => {
    userClicked(EventTypeEnum.FIVE_MINUTE_REMINDER_TOAST_SCHEDULING_PROMPT, {
      patientShortId: patientShortId ?? '',
      providerShortId: providerShortId ?? '',
    });
  });
  trackSchedulingPromptRef.current = () => {
    userClicked(EventTypeEnum.FIVE_MINUTE_REMINDER_TOAST_SCHEDULING_PROMPT, {
      patientShortId: patientShortId ?? '',
      providerShortId: providerShortId ?? '',
    });
  };

  const getRebookingNudgeParams = () => ({
    appointmentShortId: appointmentShortIdRef.current,
    patientShortId: patientShortId ?? '',
    providerShortId: providerShortId ?? '',
    sessionType: 'intake' as const,
    hasFutureAppointment: hasFollowUpScheduledRef.current,
    experimentGroup: String(shouldSeeIntakeNudgeRef.current),
  });

  const showToast = () => {
    const isIntakeRebookingNudge = shouldSeeIntakeNudgeRef.current && isIntakeAppointmentRef.current;
    const nudgeActedOnRef = { current: false };

    if (isIntakeRebookingNudge) {
      userViewed(EventTypeEnum.TELEHEALTH_REBOOKING_NUDGE_SHOWN, getRebookingNudgeParams());
    }

    toast.enqueue(
      ({ closeToast, ...ariaProps }) => {
        if (shouldSeeIntakeNudgeRef.current && isIntakeAppointmentRef.current) {
          if (hasFollowUpScheduledRef.current && nextAppointmentRef.current?.timeStart) {
            const formattedDateTime = formatNextAppointmentDateTime(String(nextAppointmentRef.current.timeStart));

            return (
              <PromptFollowUpToast
                {...ariaProps}
                title={t('telehealth-core:promptFollowUpToast.intakeFollowUpScheduledTitle')}
                subtitle={t('telehealth-core:promptFollowUpToast.intakeFollowUpScheduledSubtitle', {
                  formattedDateTime,
                })}
              />
            );
          }

          return (
            <PromptFollowUpToast
              {...ariaProps}
              title={t('telehealth-core:promptFollowUpToast.intakeNoFollowUpTitle')}
              subtitle={t('telehealth-core:promptFollowUpToast.intakeNoFollowUpSubtitle', {
                clientFirstName: clientFirstNameRef.current,
              })}
              callToAction={t('telehealth-core:promptFollowUpToast.scheduleAppointmentCta')}
              onCallToAction={() => {
                closeToast();
                nudgeActedOnRef.current = true;
                openSchedulingDrawerRef.current();
                userClicked(EventTypeEnum.TELEHEALTH_REBOOKING_NUDGE_CLICKED, {
                  ...getRebookingNudgeParams(),
                  ctaType: 'schedule',
                });
              }}
            />
          );
        }

        return (
          <PromptFollowUpToast
            {...ariaProps}
            title={t('telehealth-core:promptFollowUpToast.fiveMinuteReminderTitle')}
            subtitle={
              <Translate
                i18nKey='telehealth-core:promptFollowUpToast.fiveMinuteReminderSubtitle'
                components={{
                  1: (
                    <Button
                      onClick={() => {
                        closeToast();
                        openAppointmentsRef.current();
                        trackSchedulingPromptRef.current();
                      }}
                      use='unstyled'
                      className='underline decoration-neutral-600 inline p-0 arc:p-0 arc:min-h-[unset]'
                      size='sm'
                    />
                  ),
                }}
              />
            }
          />
        );
      },
      {
        onClose: () => {
          if (isIntakeRebookingNudge && !nudgeActedOnRef.current) {
            userClicked(EventTypeEnum.TELEHEALTH_REBOOKING_NUDGE_DISMISSED, getRebookingNudgeParams());
          }
        },
        purpose: 'prompt',
        timeout: minutesToMilliseconds(1),
      },
    );
  };

  useEndOfSessionToast(appointmentEndTime, showToast, NUDGE_PRIOR_END_TIME_MS, getIsInSession());
}
